import { COIN_RULES, COIN_VALUE, computeCoinAward, type CoinAwardResult } from '@lk/shared';

export interface EstimateCoinsArgs {
  /** Смуга складності питання: easy = 1 бал, hard = 2 бали. */
  band: 'easy' | 'hard';
  /** Ставка саме цього питання, як її оголосив сервер разом із сесією. */
  coinValue: number;
  isCorrect: boolean;
  responseMs: number;
  hintsUsed: number;
  hintsAvailable: number;
  /** Скільки вже зароблено сьогодні (з гаманця). Невідомо — вважаємо 0. */
  earnedToday?: number;
  /** Денний ліміт із гаманця. Невідомо — не обмежуємо. */
  dailyCap?: number;
}

/**
 * ОПТИМІСТИЧНИЙ показ монет одразу після відповіді.
 *
 * Це навмисно «≈»: клієнт не знає ні про слабкий предмет, ні про повторне
 * влучання в те саме питання, ні про бонус за виправлення помилки.
 * Щойно приходить відповідь сервера — вона беззастережно замінює це число.
 * Локальний підрахунок НІКОЛИ не стає джерелом правди.
 */
export function estimateCoins(args: EstimateCoinsArgs): CoinAwardResult {
  const dailyCap = args.dailyCap ?? Number.MAX_SAFE_INTEGER;
  const earnedToday = args.earnedToday ?? 0;

  const base = computeCoinAward({
    band: args.band,
    isCorrect: args.isCorrect,
    responseMs: args.responseMs,
    hintsUsed: args.hintsUsed,
    hintsAvailable: args.hintsAvailable,
    alreadyPaidRecently: false,
    isWeakSubject: false,
    isRedemption: false,
    earnedToday,
    dailyCap,
  });

  if (base.coins === 0) return base;

  // Сервер міг оголосити для цього питання іншу ставку (наприклад, із множником
  // за слабкий предмет). Довіряємо оголошеній ставці, а не власній арифметиці.
  const declared = args.coinValue > 0 ? args.coinValue : COIN_VALUE[args.band];
  if (declared === base.coins) return base;

  const remaining = Math.max(0, dailyCap - earnedToday);
  const capped = declared > remaining;
  return {
    coins: capped ? remaining : declared,
    reasons: capped ? [...base.reasons, 'daily_cap'] : base.reasons,
    cappedByDailyLimit: capped,
  };
}

/** Чи «згорить» бал, якщо відкрити ще одну підказку. */
export function willZeroOutCoins(hintsUsed: number, hintsAvailable: number): boolean {
  if (!COIN_RULES.ZERO_COIN_IF_ALL_HINTS) return false;
  return hintsAvailable > 0 && hintsUsed + 1 >= hintsAvailable;
}

/** Людський підпис причини нарахування — те, що дитина бачить у фідбеку. */
export const COIN_REASON_LABELS: Record<string, string> = {
  easy_question: 'за просте питання',
  hard_question: 'за складне питання',
  weak_subject_boost: 'множник за слабкий предмет',
  redemption: 'виправив колишню помилку',
  daily_cap: 'денний ліміт',
  too_fast: 'занадто швидко — схоже на навмання',
  already_paid: 'за це питання бал уже нараховували',
  all_hints_used: 'використані всі підказки',
  session_bonus: 'бонус за пройдену сесію',
  goal_bonus: 'бонус за денну ціль',
};

export function coinReasonLabel(reason: string): string {
  return COIN_REASON_LABELS[reason] ?? reason;
}
